"use client";

import { c as _c } from "react/compiler-runtime";
import dynamic from "next/dynamic";
import Link from "next/link";
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from "motion/react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { BoyTripsLogo } from "./BoyTripsLogo";
import { ContinueGroups } from "./ContinueGroups";
import { GuestOverview, LaunchShowcase, MemoriesShowcase, MoneyShowcase, PlannerShowcase, ReceiptShowcase, StayShowcase } from "./FeatureSections";
import { ArrowRightIcon, ChevronDownIcon } from "./LandingIcons";
import { guestSections, memberSections } from "./landing-data";
import type { BoyTripsLandingProps, GlobePointer, LandingRoutes } from "./landing-types";
import { AuthModalRequest, AuthModal } from "../auth/auth-modal";
import { useUserSession } from "../contexts/user-context";
import { UserAvatar } from "../user/user-avatar";
import { UserMenu } from "../user/user-menu";
import { ThemeToggle } from "../theme-toggle";
import { hasAdminAccess } from "@/data/account-types";
export type { BoyTripsLandingProps } from "./landing-types";
const TravelGlobe = dynamic(() => import("./TravelGlobe").then(module => module.TravelGlobe), {
  ssr: false,
  loading: () => <div className="absolute inset-0 bg-surface" />
});
const LANDING_STATE_KEY = "boyTrips:landingState";
const DEFAULT_ROUTES = {
  groups: "/groups",
  newGroup: "/groups/new",
  trips: "/trips"
} satisfies LandingRoutes;
type SavedLandingState = {
  sectionId: string;
  sectionOffsetPx: number;
  groupId?: string;
  savedAt: number;
};
type SectionNode = HTMLElement & {
  dataset: {
    landingSection?: string;
  };
};
function getSectionNodes(root: HTMLElement) {
  return Array.from(root.querySelectorAll<SectionNode>("[data-landing-section]"));
}
function getNearestSectionIndex(root: HTMLElement, sections: SectionNode[]) {
  if (sections.length === 0) return 0;
  const viewportCenter = root.scrollTop + root.clientHeight * 0.5;
  let closestIndex = 0;
  let closestDistance = Number.POSITIVE_INFINITY;
  sections.forEach((section, index) => {
    const visibleHeight = Math.min(section.offsetHeight, root.clientHeight);
    const center = section.offsetTop + visibleHeight * 0.5;
    const distance = Math.abs(center - viewportCenter);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  });
  return closestIndex;
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function isSavedLandingState(value: unknown): value is SavedLandingState {
  if (!isRecord(value)) return false;
  return typeof value.sectionId === "string" && typeof value.sectionOffsetPx === "number" && Number.isFinite(value.sectionOffsetPx) && typeof value.savedAt === "number" && Number.isFinite(value.savedAt) && (value.groupId === undefined || typeof value.groupId === "string");
}
function readSavedState() {
  try {
    const raw = window.sessionStorage.getItem(LANDING_STATE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (isSavedLandingState(parsed)) return parsed;
  } catch {
    window.sessionStorage.removeItem(LANDING_STATE_KEY);
    return null;
  }
  window.sessionStorage.removeItem(LANDING_STATE_KEY);
  return null;
}
function easeOutCubic(progress: number) {
  return 1 - (1 - progress) ** 3;
}
function SectionFrame(t0) {
  const $ = _c(14);
  const {
    id,
    index,
    activeIndex,
    desktopPager,
    children,
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const active = !desktopPager || activeIndex === index;
  const direction = index > activeIndex ? 1 : -1;
  const layoutClass = desktopPager ? "min-h-svh snap-start items-center" : id === "home" ? "min-h-svh items-stretch" : "items-start";
  const t2 = `relative flex scroll-mt-20 px-4 pb-16 pt-24 sm:px-6 sm:pb-20 sm:pt-28 lg:px-8 ${layoutClass} ${className}`;
  let t3;
  if ($[0] !== active || $[1] !== direction) {
    t3 = active ? {
      opacity: 1,
      y: 0,
      scale: 1
    } : {
      opacity: 0.18,
      y: direction * 22,
      scale: 0.99
    };
    $[0] = active;
    $[1] = direction;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = desktopPager ? 0.46 : 0.18;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [0.22, 1, 0.36, 1];
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t4) {
    t6 = {
      duration: t4,
      ease: t5
    };
    $[4] = t4;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== children || $[7] !== t3 || $[8] !== t6) {
    t7 = <motion.div animate={t3} transition={t6} className="mx-auto w-full max-w-[1440px]">{children}</motion.div>;
    $[6] = children;
    $[7] = t3;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== id || $[11] !== t2 || $[12] !== t7) {
    t8 = <section data-landing-section={id} className={t2}>{t7}</section>;
    $[10] = id;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
export function BoyTripsLanding({
  viewer = null,
  routes: routeOverrides,
  globeHotspotRadiusPx = 92
}: BoyTripsLandingProps) {
  const user = useUserSession();
  const authenticated = Boolean(user);
  const canAdmin = hasAdminAccess(user?.accountType);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<GlobePointer>({
    clientX: 0,
    clientY: 0,
    active: false,
    coarse: false
  });
  const scrollAnimationRef = useRef<number | null>(null);
  const scrollAnimatingRef = useRef(false);
  const wheelCooldownUntilRef = useRef(0);
  const activeIndexRef = useRef(0);
  const restoredRef = useRef(false);
  const pendingGroupIdRef = useRef<string | null>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reducedMotion = Boolean(useReducedMotion());
  const [activeSection, setActiveSection] = useState("home");
  const [focusedGroupId, setFocusedGroupId] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  const [desktopPager, setDesktopPager] = useState(false);
  const [authRequest, setAuthRequest] = useState<AuthModalRequest | null>(null);
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px) and (pointer: fine)");
    const update = () => setDesktopPager(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  const navigation = useMemo(() => [...(authenticated ? memberSections : guestSections)], [authenticated]);
  const resolvedRoutes = {
    ...DEFAULT_ROUTES,
    ...routeOverrides
  };
  const activeIndex = Math.max(0, navigation.findIndex(section => section.id === activeSection));
  const {
    scrollYProgress
  } = useScroll({
    container: scrollerRef
  });
  const rawGlobeOpacity = useTransform(scrollYProgress, [0, 0.08, 0.24, 0.6, 1], [1, 0.96, 0.54, 0.3, 0.18]);
  const rawGlobeScale = useTransform(scrollYProgress, [0, 0.25, 1], [1, 1.018, 1.055]);
  const rawVeilOpacity = useTransform(scrollYProgress, [0, 0.1, 0.34, 1], [0, 0.16, 0.5, 0.74]);
  const globeOpacity = useSpring(rawGlobeOpacity, {
    stiffness: 110,
    damping: 24,
    mass: 0.3
  });
  const globeScale = useSpring(rawGlobeScale, {
    stiffness: 105,
    damping: 25,
    mass: 0.3
  });
  const veilOpacity = useSpring(rawVeilOpacity, {
    stiffness: 100,
    damping: 24,
    mass: 0.32
  });
  const cancelScrollAnimation = useCallback(() => {
    if (scrollAnimationRef.current !== null) {
      window.cancelAnimationFrame(scrollAnimationRef.current);
      scrollAnimationRef.current = null;
    }
    scrollerRef.current?.style.removeProperty("scroll-snap-type");
    scrollAnimatingRef.current = false;
  }, []);
  const writeSavedState = useCallback((groupId = pendingGroupIdRef.current) => {
    const root = scrollerRef.current;
    if (!root || !restoredRef.current) return;
    const sections = getSectionNodes(root);
    const index = getNearestSectionIndex(root, sections);
    const section_0 = sections[index];
    const sectionId = section_0?.dataset.landingSection;
    if (!section_0 || !sectionId) return;
    const maxOffset = Math.max(0, section_0.offsetHeight - root.clientHeight);
    const sectionOffsetPx = Math.max(0, Math.min(maxOffset, root.scrollTop - section_0.offsetTop));
    const state: SavedLandingState = {
      sectionId,
      sectionOffsetPx,
      savedAt: Date.now(),
      ...(groupId ? {
        groupId
      } : {})
    };
    window.sessionStorage.setItem(LANDING_STATE_KEY, JSON.stringify(state));
  }, []);
  const scrollToSection = useCallback((sectionId_0: string, smooth = true) => {
    const root_0 = scrollerRef.current;
    const section_1 = root_0?.querySelector<HTMLElement>(`[data-landing-section="${sectionId_0}"]`);
    if (!root_0 || !section_1) return;
    cancelScrollAnimation();
    const target = section_1.offsetTop;
    const start = root_0.scrollTop;
    const distance = target - start;
    if (!desktopPager) {
      root_0.scrollTo({
        top: target,
        behavior: smooth && !reducedMotion ? "smooth" : "auto"
      });
      return;
    }
    if (!smooth || reducedMotion || Math.abs(distance) < 2) {
      root_0.scrollTop = target;
      return;
    }
    const duration = Math.min(780, Math.max(520, 520 + Math.abs(distance / Math.max(1, root_0.clientHeight)) * 70));
    const startedAt = performance.now();
    root_0.style.scrollSnapType = "none";
    scrollAnimatingRef.current = true;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      root_0.scrollTop = start + distance * easeOutCubic(progress);
      if (progress < 1) {
        scrollAnimationRef.current = window.requestAnimationFrame(tick);
        return;
      }
      scrollAnimationRef.current = null;
      scrollAnimatingRef.current = false;
      root_0.scrollTop = target;
      root_0.style.removeProperty("scroll-snap-type");
      wheelCooldownUntilRef.current = performance.now() + 120;
    };
    scrollAnimationRef.current = window.requestAnimationFrame(tick);
  }, [cancelScrollAnimation, desktopPager, reducedMotion]);
  useLayoutEffect(() => {
    const root_1 = scrollerRef.current;
    if (!root_1) return;
    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    restoredRef.current = false;
    setRestored(false);
    const restore = () => {
      const sections_0 = getSectionNodes(root_1);
      const saved = readSavedState();
      const validGroupId = saved?.groupId && viewer?.groups.some(group => group.id === saved.groupId) ? saved.groupId : null;
      const requestedSectionId = validGroupId ? "continue" : saved?.sectionId ?? "home";
      const section_2 = sections_0.find(node => node.dataset.landingSection === requestedSectionId) ?? sections_0[0];
      const sectionId_1 = section_2?.dataset.landingSection ?? "home";
      if (section_2) {
        const maxOffset_0 = Math.max(0, section_2.offsetHeight - root_1.clientHeight);
        const offset = validGroupId ? 0 : Math.max(0, Math.min(maxOffset_0, saved?.sectionOffsetPx ?? 0));
        root_1.scrollTop = section_2.offsetTop + offset;
      }
      const nextIndex = Math.max(0, sections_0.findIndex(node_0 => node_0.dataset.landingSection === sectionId_1));
      activeIndexRef.current = nextIndex;
      setActiveSection(sectionId_1);
      setFocusedGroupId(validGroupId);
      pendingGroupIdRef.current = validGroupId;
      window.requestAnimationFrame(() => {
        restoredRef.current = true;
        setRestored(true);
        if (validGroupId) {
          window.requestAnimationFrame(() => {
            const groupCard = document.getElementById(`landing-group-${validGroupId}`);
            groupCard?.focus({
              preventScroll: true
            });
            pendingGroupIdRef.current = null;
            writeSavedState(null);
          });
        }
      });
    };
    restore();
    window.addEventListener("pageshow", restore);
    return () => {
      window.removeEventListener("pageshow", restore);
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, [viewer, writeSavedState]);
  useEffect(() => {
    const root_2 = scrollerRef.current;
    if (!root_2) return;
    let frame = 0;
    const updateActiveSection = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const sections_1 = getSectionNodes(root_2);
        const index_0 = getNearestSectionIndex(root_2, sections_1);
        const sectionId_2 = sections_1[index_0]?.dataset.landingSection;
        if (sectionId_2 && index_0 !== activeIndexRef.current) {
          activeIndexRef.current = index_0;
          setActiveSection(sectionId_2);
        }
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(() => writeSavedState(), 100);
      });
    };
    const saveImmediately = () => writeSavedState();
    root_2.addEventListener("scroll", updateActiveSection, {
      passive: true
    });
    window.addEventListener("pagehide", saveImmediately);
    return () => {
      root_2.removeEventListener("scroll", updateActiveSection);
      window.removeEventListener("pagehide", saveImmediately);
      window.cancelAnimationFrame(frame);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [writeSavedState]);
  useEffect(() => {
    const root_3 = scrollerRef.current;
    if (!root_3 || !desktopPager) return;
    const pointerQuery = window.matchMedia("(pointer: coarse)");
    let wheelAccumulator = 0;
    let resetTimer: ReturnType<typeof setTimeout> | null = null;
    const onWheel = (event: WheelEvent) => {
      if (pointerQuery.matches || event.ctrlKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      event.preventDefault();
      if (scrollAnimatingRef.current || performance.now() < wheelCooldownUntilRef.current) return;
      const multiplier = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? root_3.clientHeight : 1;
      wheelAccumulator += event.deltaY * multiplier;
      if (resetTimer) clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        wheelAccumulator = 0;
      }, 130);
      if (Math.abs(wheelAccumulator) < 24) return;
      const sections_2 = getSectionNodes(root_3);
      const currentIndex = getNearestSectionIndex(root_3, sections_2);
      const nextIndex_0 = Math.max(0, Math.min(sections_2.length - 1, currentIndex + Math.sign(wheelAccumulator)));
      wheelAccumulator = 0;
      const nextId = sections_2[nextIndex_0]?.dataset.landingSection;
      if (nextId && nextIndex_0 !== currentIndex) scrollToSection(nextId);
    };
    root_3.addEventListener("wheel", onWheel, {
      passive: false
    });
    return () => {
      root_3.removeEventListener("wheel", onWheel);
      if (resetTimer) clearTimeout(resetTimer);
    };
  }, [desktopPager, scrollToSection]);
  useEffect(() => {
    if (!desktopPager) return;
    const onKeyDown = (event_0: KeyboardEvent) => {
      const target_0 = event_0.target;
      if (target_0 instanceof HTMLInputElement || target_0 instanceof HTMLTextAreaElement || target_0 instanceof HTMLElement && target_0.isContentEditable) return;
      const root_4 = scrollerRef.current;
      if (!root_4) return;
      const sections_3 = getSectionNodes(root_4);
      const currentIndex_0 = getNearestSectionIndex(root_4, sections_3);
      let nextIndex_1: number | null = null;
      if (event_0.key === "ArrowDown" || event_0.key === "PageDown" || event_0.key === " " && !event_0.shiftKey) nextIndex_1 = currentIndex_0 + 1;
      if (event_0.key === "ArrowUp" || event_0.key === "PageUp" || event_0.key === " " && event_0.shiftKey) nextIndex_1 = currentIndex_0 - 1;
      if (event_0.key === "Home") nextIndex_1 = 0;
      if (event_0.key === "End") nextIndex_1 = sections_3.length - 1;
      if (nextIndex_1 === null) return;
      event_0.preventDefault();
      const clampedIndex = Math.max(0, Math.min(sections_3.length - 1, nextIndex_1));
      const nextId_0 = sections_3[clampedIndex]?.dataset.landingSection;
      if (nextId_0) scrollToSection(nextId_0);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [desktopPager, scrollToSection]);
  useEffect(() => {
    const query_0 = window.matchMedia("(pointer: coarse)");
    const update_0 = () => {
      pointerRef.current.coarse = query_0.matches;
      if (query_0.matches) pointerRef.current.active = false;
    };
    update_0();
    query_0.addEventListener("change", update_0);
    return () => query_0.removeEventListener("change", update_0);
  }, []);
  useEffect(() => cancelScrollAnimation, [cancelScrollAnimation]);
  const updatePointer = (event_1: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerRef.current.coarse || event_1.pointerType === "touch") return;
    pointerRef.current.clientX = event_1.clientX;
    pointerRef.current.clientY = event_1.clientY;
    pointerRef.current.active = true;
  };
  const saveReturnGroup = (groupId_0: string) => {
    pendingGroupIdRef.current = groupId_0;
    setFocusedGroupId(groupId_0);
    writeSavedState(groupId_0);
  };
  const openAuth = useCallback((mode: "login" | "signup") => {
    writeSavedState();
    setAuthRequest({
      mode
    });
  }, [writeSavedState]);
  const closeAuth = useCallback(() => setAuthRequest(null), []);
  const firstContentSection = authenticated ? "continue" : "overview";
  return <div className="relative h-svh overflow-hidden bg-bg text-text-primary selection:bg-brand-primary selection:text-text-on-brand lg:min-h-[620px]" onPointerMove={updatePointer} onPointerEnter={updatePointer} onPointerLeave={() => {
    pointerRef.current.active = false;
  }}>
            <motion.div className="pointer-events-none fixed inset-0 z-0 will-change-transform" style={{
      opacity: globeOpacity,
      scale: globeScale
    }}>
                <TravelGlobe pointerRef={pointerRef} reducedMotion={reducedMotion} hotspotRadiusPx={globeHotspotRadiusPx} />
            </motion.div>

            <div className="pointer-events-none fixed inset-0 z-[1] bg-[radial-gradient(circle_at_12%_10%,var(--color-brand-primary-soft),transparent_34%),radial-gradient(circle_at_88%_8%,var(--color-brand-secondary-soft),transparent_30%),radial-gradient(circle_at_55%_100%,var(--color-brand-accent-soft),transparent_28%)] opacity-90" />
            <div className="pointer-events-none fixed inset-0 z-[1] bg-gradient-to-r from-bg/95 via-bg/72 to-bg/10 max-md:bg-gradient-to-b max-md:from-bg/10 max-md:via-bg/25 max-md:to-bg/95" />
            <div className="pointer-events-none fixed inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-bg/70" />
            <motion.div className="pointer-events-none fixed inset-0 z-[2] bg-bg" style={{
      opacity: veilOpacity
    }} />
            <div className="pointer-events-none fixed inset-0 z-[3] opacity-[0.035] mix-blend-screen" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.8'/%3E%3C/svg%3E\")"
    }} />

            <header className="fixed inset-x-0 top-0 z-40 border-b border-border bg-surface-glass backdrop-blur-xl">
                <div className="mx-auto flex h-20 w-full max-w-[1440px] items-center justify-between px-5 sm:px-8">
                    <button type="button" onClick={() => scrollToSection("home")} className="rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-focus/35">
                        <BoyTripsLogo compact />
                    </button>

                    <nav className="hidden items-center gap-7 text-sm font-bold text-text-primary/55 md:flex">
                        <button type="button" onClick={() => scrollToSection(firstContentSection)} className="transition hover:text-text-primary">{authenticated ? "Your trips" : "How it works"}</button>
                        <button type="button" onClick={() => scrollToSection("plan")} className="transition hover:text-text-primary">AI planning</button>
                        <button type="button" onClick={() => scrollToSection("money")} className="transition hover:text-text-primary">Finances</button>
                        <button type="button" onClick={() => scrollToSection("receipts")} className="transition hover:text-text-primary">Receipts</button>
                        <button type="button" onClick={() => scrollToSection("memories")} className="transition hover:text-text-primary">Media</button>
                    </nav>

                    <div className="flex items-center gap-2">
                        <ThemeToggle />
                        {user ? <>
                                {canAdmin && <Link href="/admin" className="hidden rounded-full border border-border-brand bg-brand-primary-soft px-4 py-2.5 text-sm font-black text-brand-primary transition hover:-translate-y-0.5 hover:bg-brand-primary-soft/70 sm:inline-flex">Admin Menu</Link>}
                                <UserMenu user={user} tripsHref={resolvedRoutes.trips} newTripHref={`${resolvedRoutes.trips.replace(/\/$/, "")}/new`} newGroupHref={resolvedRoutes.newGroup} />
                                <Link href={resolvedRoutes.trips} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-4 py-2.5 text-sm font-black text-text-on-brand transition hover:-translate-y-0.5 hover:bg-brand-primary-hover">
                                    View Trips
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Link>
                            </> : <>
                                <button type="button" data-overlay-target onClick={() => openAuth("login")} className="hidden rounded-full px-4 py-2 text-sm font-bold text-text-primary/65 transition hover:text-text-primary sm:inline-flex">Sign in</button>
                                <button type="button" data-overlay-target onClick={() => openAuth("signup")} className="inline-flex items-center gap-2 rounded-full bg-brand-primary px-4 py-2.5 text-sm font-black text-text-on-brand transition hover:-translate-y-0.5 hover:bg-brand-primary-hover">
                                    Get started
                                    <ArrowRightIcon className="h-4 w-4" />
                                </button>
                            </>}
                    </div>
                </div>
            </header>

            <div className="fixed right-5 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-3 lg:flex">
                {navigation.map(section_3 => <button key={section_3.id} type="button" onClick={() => scrollToSection(section_3.id)} className="group relative grid h-5 w-5 place-items-center" aria-label={`Go to ${section_3.label}`} aria-current={activeSection === section_3.id ? "step" : undefined}>
                        <span className="absolute right-7 rounded-full border border-border bg-surface/90 px-2.5 py-1 text-[10px] font-black text-text-primary/65 opacity-0 backdrop-blur-xl transition group-hover:translate-x-0 group-hover:opacity-100">{section_3.label}</span>
                        <span className={`rounded-full transition-all duration-300 ${activeSection === section_3.id ? "h-2.5 w-2.5 bg-brand-primary shadow-brand-glow" : "h-1.5 w-1.5 bg-brand-primary/25 group-hover:bg-brand-primary/70"}`} />
                    </button>)}
            </div>

            <div ref={scrollerRef} className={`relative z-10 h-svh overflow-x-hidden overflow-y-auto overscroll-y-contain touch-pan-y [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:min-h-[620px] ${desktopPager ? "snap-y snap-mandatory" : "snap-none"}`} style={{
      opacity: restored ? 1 : 0,
      transition: "opacity 180ms ease"
    }}>
                <SectionFrame id="home" index={0} activeIndex={activeIndex} desktopPager={desktopPager} className="pt-24">
                    <div className="grid min-h-[calc(100svh-6rem)] items-center lg:grid-cols-[0.82fr_1.18fr]">
                        <div className="relative z-10 max-w-2xl py-10 lg:py-0">
                            <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-border-brand/50 bg-brand-primary-soft px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-brand-primary sm:text-[11px]">
                                <span className="h-1.5 w-1.5 rounded-full bg-brand-primary shadow-brand-glow" />
                                Group trips without the chaos
                            </div>

                            <BoyTripsLogo />
                            <h1 className="mt-8 max-w-xl text-5xl font-black leading-[0.94] tracking-[-0.066em] text-text-primary sm:text-7xl lg:text-[5.6rem]">
                                Plan less. <span className="text-brand-gradient">Go farther.</span>
                            </h1>
                            <p className="mt-6 max-w-xl text-base leading-7 text-text-primary/60 sm:text-lg sm:leading-8">
                                Create the group, build the gameplan, find the stay, split every dollar, analyze receipts, and keep the memories with the people who were there.
                            </p>

                            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                {authenticated ? <Link href={resolvedRoutes.trips} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 text-sm font-black text-text-on-brand transition hover:-translate-y-0.5 hover:bg-brand-primary-hover">
                                        View Trips
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </Link> : <button type="button" data-overlay-target onClick={() => openAuth("signup")} className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 text-sm font-black text-text-on-brand transition hover:-translate-y-0.5 hover:bg-brand-primary-hover">
                                        Start a Boy Trip
                                        <ArrowRightIcon className="h-4 w-4" />
                                    </button>}
                                <button type="button" onClick={() => scrollToSection(firstContentSection)} className="inline-flex items-center justify-center gap-2 rounded-full border border-border bg-surface/65 px-6 py-3.5 text-sm font-bold text-text-primary transition hover:bg-surface">
                                    {authenticated ? "Explore features" : "See how it works"}
                                    <ChevronDownIcon className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="mt-8 hidden items-center gap-3 text-xs font-bold text-text-primary/40 sm:flex">
                                <span className="h-px w-10 bg-gradient-to-r from-brand-primary to-transparent" />
                                Move your cursor near a route to light its closest point
                            </div>
                        </div>
                    </div>
                </SectionFrame>

                {authenticated ? <SectionFrame id="continue" index={1} activeIndex={activeIndex} desktopPager={desktopPager}>
                        {viewer ? <ContinueGroups viewer={viewer} focusedGroupId={focusedGroupId} newGroupHref={resolvedRoutes.newGroup} onOpenGroup={saveReturnGroup} /> : <div className="mx-auto max-w-3xl rounded-[32px] border border-border bg-surface/75 p-8 text-center shadow-card backdrop-blur-xl sm:p-12">
                                {user && <UserAvatar user={user} size={64} className="mx-auto" />}
                                <h2 className="mt-5 text-4xl font-black tracking-[-0.05em] text-text-primary sm:text-5xl">Welcome back{user?.displayName ? `, ${user.displayName}` : ''}.</h2>
                                <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-text-muted">Your groups, upcoming trips, balances, plans, and memories are waiting.</p>
                                <Link href={resolvedRoutes.trips} className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 text-sm font-black text-text-on-brand transition hover:-translate-y-0.5 hover:bg-brand-primary-hover">View Trips<ArrowRightIcon className="size-4" /></Link>
                            </div>}
                    </SectionFrame> : <SectionFrame id="overview" index={1} activeIndex={activeIndex} desktopPager={desktopPager}>
                        <GuestOverview />
                    </SectionFrame>}

                <SectionFrame id="plan" index={2} activeIndex={activeIndex} desktopPager={desktopPager}>
                    <PlannerShowcase />
                </SectionFrame>

                <SectionFrame id="stays" index={3} activeIndex={activeIndex} desktopPager={desktopPager}>
                    <StayShowcase />
                </SectionFrame>

                <SectionFrame id="money" index={4} activeIndex={activeIndex} desktopPager={desktopPager}>
                    <MoneyShowcase />
                </SectionFrame>

                <SectionFrame id="receipts" index={5} activeIndex={activeIndex} desktopPager={desktopPager}>
                    <ReceiptShowcase />
                </SectionFrame>

                <SectionFrame id="memories" index={6} activeIndex={activeIndex} desktopPager={desktopPager}>
                    <MemoriesShowcase />
                </SectionFrame>

                <SectionFrame id="launch" index={7} activeIndex={activeIndex} desktopPager={desktopPager}>
                    <LaunchShowcase authenticated={authenticated} newGroupHref={resolvedRoutes.newGroup} onSignup={() => openAuth("signup")} onLogin={() => openAuth("login")} />
                </SectionFrame>
            </div>

            {authRequest && <AuthModal request={authRequest} onClose={closeAuth} onAuthenticated={closeAuth} />}

            <div className="pointer-events-none fixed bottom-6 left-1/2 z-20 hidden -translate-x-1/2 items-center gap-3 text-[10px] font-black uppercase tracking-[0.2em] text-text-primary/35 sm:flex">
                <span className="h-8 w-px overflow-hidden bg-text-primary/10"><motion.span className="block h-1/2 w-px bg-gradient-to-b from-transparent to-brand-primary" animate={{
          y: ["-100%", "220%"]
        }} transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: "easeInOut"
        }} /></span>
                Scroll to explore
            </div>
        </div>;
}